import React, { createContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'

import { SessionStorage } from '../../../common/cookie-storage'
import { STORAGEKEYS } from '../../../common/keys'
import { AppLinks } from '../../../environment/app-links'
import { IAppRootLogicReturnModel } from '../models/IAppRootModel'
import { AppState, apphistory, store } from '../../../store'
import { AppStorage } from '../../../common/app-storage'
import { reconnectOnInvalidConnection } from '../../../store/middleware/credential.action'
import { APP_BACK_ONLINE, APP_OFFLINE, APP_ONLINE } from '../../../store/actions/application-actions'
import analyticsService from '../../../service/analytics.service'


/**
 * CONTEXT dla mechanizmu blokowania przechodzenia przez formularz "z palca"
 */
export const CurrentFormUrlPathContext = createContext({
   currentUrlFormPath: AppLinks.basicInfoForm,
   setCurrentUrlFormPath: (newValue: string) => {}
 })
 

 /**
  * Custom hook obsługujący logikę strony bazowej - AppRoot
  * @returns 
  */
const useAppRootLogic = (): IAppRootLogicReturnModel => {

   const dispatch = useDispatch()
   const sessionStorage = new SessionStorage();

   const { isOffline, wasOffline } = useSelector((state: AppState) => state.applicationState)
   const IS_APPLICATION_HAS_GLOBAL_ERROR = useSelector((state: AppState) => state.applicationState.isAppHasError)
   const ANALYTICS_EVENT_TYPES_NAMES = useSelector((state: AppState) => state.lenConfigState.analyticsEventTypesNames) 
   const USER_TRAPPER_ON_PAGE_URL_PATH = useSelector((state: AppState) => state.applicationState.userTrappedOnPageUrlPath)
   const [currentLocation, setLocation] = useState("");
   const [currentUrlFormPath, setCurrentUrlFormPath] = useState(sessionStorage?.get(STORAGEKEYS.SET_CURRENT_FORM_URL_PAGE_PATH ?? ""))


// ============== USE EFFECT'S ============== //


   /**
    * Logika blokująca użytkownika na konkretnej stronie w aplikacji. Mechanizm wykorzystywany m.in. na stronie z odmową, tak aby użytkownik nie mógł się cofnąć 
    * do poprzednich stron procesu i został przekierowany na link z leadem.
   */
   useEffect(() => {
      const unlisten = apphistory.listen((location) => {
         if (USER_TRAPPER_ON_PAGE_URL_PATH !== undefined && location.pathname !== USER_TRAPPER_ON_PAGE_URL_PATH) {           
            apphistory.replace(USER_TRAPPER_ON_PAGE_URL_PATH)
         }
      })

      return () => unlisten()
   }, [ USER_TRAPPER_ON_PAGE_URL_PATH ])


   useEffect(() => {
      const intervalId = setInterval(() => {
         const state = store.getState()

         if (state.applicationState.isAppHasError) { // W momencie wystąpienia globalnego błędu w aplikacji zatrzymujemy interwał i więcej nie próbujemy połączyć się z HUBem
            clearInterval(intervalId) // Zatrzymujemy interwał
            return
         }

         dispatch(reconnectOnInvalidConnection())
      }, 30000)
      
      apphistory.listen((location, action) => {
         if (action === "PUSH") {
            const targetLocation = `${location.pathname}${location.search ?? ""}${location.hash ?? ""}`

            if(!location.pathname.includes(AppLinks.houston)){
               setLocation(targetLocation)
            }

            const currentBrowserLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`
            if(targetLocation !== currentBrowserLocation){
               apphistory.push(targetLocation)
            }

            window.scrollTo(0,0)
         }
         else {
            apphistory.go(1)
         }
      })

      apphistory.go(1)

      const storage = new SessionStorage();
      const token = storage.get(STORAGEKEYS.ACCESS_TOKEN)
      
      if(token === null){
         const appStorage = new AppStorage();
         appStorage.clearStore();
      }

      return () => {
         if(intervalId){
            clearInterval(intervalId)
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      const storage = new SessionStorage();
      const formPathFromStorage = storage.get(STORAGEKEYS.SET_CURRENT_FORM_URL_PAGE_PATH)

      if(formPathFromStorage === null){ //Sytuacja w której nie ma takiego klucza w session storage (pierwsze wejście na stronę)
         storage.add(STORAGEKEYS.SET_CURRENT_FORM_URL_PAGE_PATH, AppLinks.basicInfoForm)
         setCurrentUrlFormPath(AppLinks.basicInfoForm)
      }
      else { //Sytuacja w której ścieżka uległa zmianie - zapisujemy nową wartość w session storage, tak aby po przeładowaniu strony można ją było odzyskać
         storage.add(STORAGEKEYS.SET_CURRENT_FORM_URL_PAGE_PATH, currentUrlFormPath)
      }
   }, [currentUrlFormPath])

   useEffect(() => {
      if(!isOffline && wasOffline){
         apphistory.push(currentLocation);
         dispatch({ type: APP_BACK_ONLINE })
      }

   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [ isOffline ])


   useEffect(() => {
      if(sessionStorage.get(STORAGEKEYS.CURRENT_PAGE_STEP) === null){ //Jeśli nie istnieje, tworzymy pusty wpis w sessionStorage w którym będziemy przechowywać informację o aktualnym kroku steppera
         sessionStorage.add(STORAGEKEYS.CURRENT_PAGE_STEP, "")
      }  
   }, []);



   // Mechanizm gwarantujący, że gdy tylko zostanie ustawione GLOBAL_ERROR na true, przejdziemy na stronę błędu. 
   useEffect(() => {
      if(IS_APPLICATION_HAS_GLOBAL_ERROR === true && window.location.pathname !== AppLinks.error){
         apphistory.push(AppLinks.error)
      }
   //eslint-disable-next-line react-hooks/exhaustive-deps
   }, [ IS_APPLICATION_HAS_GLOBAL_ERROR ])
   


// ============== WINDOWS LISTENER'S ============== //
   useEffect(() => {
      const handleOnline = async () => {
         if(sessionStorage.get(STORAGEKEYS.WAS_USER_ON_OFFLINE_PAGE) === "true" ) {
            sessionStorage.changeItemValue(STORAGEKEYS.WAS_USER_ON_OFFLINE_PAGE, "")

            await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.FrontendUserOfflinePageDisplayed)
         }
         
         dispatch({ type: APP_ONLINE })
      }
   
      const handleOffline = () => {
         sessionStorage.changeItemValue(STORAGEKEYS.WAS_USER_ON_OFFLINE_PAGE, "true")
         dispatch({ type: APP_OFFLINE })
         
         apphistory.push(AppLinks.houston)
      }
   
      const handlePopState = () => {
         apphistory.go(1)
      }


      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
      window.addEventListener('popstate', handlePopState)


      return () => {
         window.removeEventListener('online', handleOnline)
         window.removeEventListener('offline', handleOffline)
         window.removeEventListener('popstate', handlePopState)
      }

   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   return {
      currentUrlFormPath,
      setCurrentUrlFormPath,
   }
}


export default useAppRootLogic;